import { useSearchParams } from 'next/navigation';
import { isSignupLocalFakesBuild } from '@/services/build-environment.mjs';
import {
  useSignupFlowStore,
  type SignupLegalName,
  type SignupSignInContact,
} from '../stores/signup-flow-store';
import type { SamlContact } from '@galapagos/shared';
import type { SignedInWith } from './profile-answers';

/** The query parameter that picks the email sign-in fallback in a local-fakes build. */
export const SIGNIN_FALLBACK_PARAM = 'signin';

// Next inlines both environment values the build check reads, so every
// deployed bundle answers `false` and these fixtures are never returned there.

/** Matches the local fake's roster candidate, so a local run reads as one person. */
const FALLBACK_LEGAL_NAME: SignupLegalName = { firstName: 'Sam', lastName: 'Example' };
const FALLBACK_SMS_CONTACT: SignupSignInContact = { channel: 'sms', destination: '+15555550100' };
const FALLBACK_EMAIL_CONTACT: SignupSignInContact = {
  channel: 'email',
  destination: 'sam.example@example.com',
};

/**
 * The stored legal name, or (local-fakes builds only, until the legal-name
 * step exists) a fixture name, so the profile steps can be clicked through.
 */
export function resolveLegalName(
  stored: SignupLegalName | null,
  localFakesBuild: boolean,
): SignupLegalName | null {
  if (stored) return stored;
  return localFakesBuild ? FALLBACK_LEGAL_NAME : null;
}

/**
 * The stored sign-in contact, or (local-fakes builds only) a fixture: a phone
 * sign-in by default, an email one when the URL carries `signin=email`.
 */
export function resolveSignInContact(
  stored: SignupSignInContact | null,
  localFakesBuild: boolean,
  signinParam: string | null,
): SignupSignInContact | null {
  if (stored) return stored;
  if (!localFakesBuild) return null;
  return signinParam === 'email' ? FALLBACK_EMAIL_CONTACT : FALLBACK_SMS_CONTACT;
}

/** How an SSO member signed in: the roster contact's channel, never its destination. */
export function ssoSignedInWith(contact: SamlContact): SignedInWith {
  return { channel: contact === 'email' ? 'email' : 'sms' };
}

export function useSignupLegalName(): SignupLegalName | null {
  const stored = useSignupFlowStore((state) => state.legalName);
  return resolveLegalName(stored, isSignupLocalFakesBuild());
}

export function useSignupSignInContact(): SignupSignInContact | null {
  const stored = useSignupFlowStore((state) => state.signInContact);
  const signinParam = useSearchParams().get(SIGNIN_FALLBACK_PARAM);
  return resolveSignInContact(stored, isSignupLocalFakesBuild(), signinParam);
}
